// src/services/artistService.ts - Updated to handle custom month
import axios from 'axios';
import { API_CONFIG, getApiUrl } from '../config/api';
import { getAccessToken } from './authService';

// Types for artist data
export interface Artist {
  id: number;
  name: string;
  genre: string;
  score: number;
  previousScore: number;
  followers: number;
  monthlyListeners: number;
  totalStreams: number;
  saveRate: number;
  mostPopularTrack: string;
  mostPopularTrackStreams: number;
}

export interface ScoreHistory {
  month: string;
  year: string;
  score: number;
}

export interface Release {
  id: number;
  name: string;
  date: string;
  type: string;
}

export interface Song {
  id: number;
  title: string;
  releaseDate: string;
  streams: number;
  previousStreams: number;
  score: number;
  duration: string;
}

export interface ArtistDetails {
  artist: Artist;
  scoreHistory: ScoreHistory[];
  releases: Release[];
  songs: Song[];
  monthlyMetrics: {
    month: string;
    streams: number;
    listeners: number;
    playlists: number;
    saves: number;
    score: number;
    followers: number;
  }[];
}

// Fetch artist details including score history, releases, and songs
export const getArtistDetails = async (artistId: string): Promise<ArtistDetails> => {
  try {
    const endpoint = API_CONFIG.ENDPOINTS.ARTIST.DETAILS(artistId);
    console.log(`Fetching artist details from: ${getApiUrl(endpoint)}`);
    
    // Token aus authService holen
    const token = getAccessToken();
    
    // Konfiguration mit Authorization-Header
    const config = token ? {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    } : {};
    
    // Log zum Debugging
    console.log('Using authorization token:', token ? 'Yes' : 'No');
    
    // Request mit Konfiguration senden
    const response = await axios.get(getApiUrl(endpoint), config);
    return response.data;
  } catch (error) {
    // Verbesserte Fehlerbehandlung
    if (axios.isAxiosError(error)) {
      console.error('Axios Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data, // Hier sollten die Fehlerdetails vom Server sein
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers // Welche Header wurden gesendet?
      });
      
      // Falls der Server eine Fehlermeldung zurückgibt, diese anzeigen
      if (error.response?.data?.message) {
        console.error('Server error message:', error.response.data.message);
      }
      
      if (error.response?.data?.error) {
        console.error('Server error type:', error.response.data.error);
      }
    }
    
    console.error('Error fetching artist details:', error);
    throw error;
  }
};

// Fetch only the songs for an artist
export const getArtistSongs = async (artistId: string): Promise<Song[]> => {
  try {
    const endpoint = API_CONFIG.ENDPOINTS.ARTIST.SONGS(artistId);
    const response = await axios.get(getApiUrl(endpoint));
    return response.data;
  } catch (error) {
    console.error('Error fetching artist songs:', error);
    throw error;
  }
};

// Add new metrics for an artist
export const addArtistMetrics = async (
  artistId: string, 
  metrics: {
    streams: number;
    listeners: number;
    saves: number;
    follower: number;
    playlist_adds: number;
    notes?: string;
    metric_date?: string; // Optional parameter for custom month
  }
): Promise<{success: boolean, score?: number}> => {
  try {
    // Make sure all values are properly formatted as numbers
    const formattedMetrics = {
      streams: parseInt(String(metrics.streams), 10) || 0,
      listeners: parseInt(String(metrics.listeners), 10) || 0,
      saves: parseInt(String(metrics.saves), 10) || 0,
      follower: parseInt(String(metrics.follower), 10) || 0, // Note: Using singular "follower" to match PHP
      playlist_adds: parseInt(String(metrics.playlist_adds), 10) || 0,
      notes: metrics.notes || '',
      metric_date: metrics.metric_date || undefined // Pass the custom month if provided
    };
    
    // Check if we're using a Spotify ID or an internal ID
    // If artistId starts with a letter, it's likely a Spotify ID and we should use 'me'
    const endpoint = API_CONFIG.ENDPOINTS.ARTIST.METRICS(
      /^[a-zA-Z]/.test(artistId) ? 'me' : artistId
    );
    
    // Add custom header with the Spotify Artist ID if needed
    const headers = {};
    if (/^[a-zA-Z]/.test(artistId)) {
      // Add the actual Spotify ID in a custom header if needed
      // This might need to be implemented on the server side to handle
      // headers['X-Spotify-Artist-ID'] = artistId;
    }
    
    console.log('Sending metrics to endpoint:', endpoint);
    console.log('Metrics data:', formattedMetrics);
    
    const response = await axios.post(getApiUrl(endpoint), formattedMetrics, { headers });
    return response.data;
  } catch (error) {
    console.error('Error adding artist metrics:', error);
    
    // Provide more detailed error information
    if (axios.isAxiosError(error) && error.response) {
      console.error('Server response:', error.response.data);
      console.error('Status code:', error.response.status);
    }
    
    // Return a standardized error response
    return { success: false };
  }
};